/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable theme-colors/no-literal-colors */
import React, { ReactNode, useRef, useCallback } from 'react';
import { styled } from '@superset-ui/core';
import { Upload as AntdUpload } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import Button from 'src/components/Button';

const { Dragger } = AntdUpload;

// -- Style: dragger drop zone --

const StyledDragger = styled(Dragger)`
  &.ant-upload-drag {
    border: ${({ theme }) => theme.gridUnit * 0.5}px dashed
      ${({ theme }) => theme.colors.grayscale.light2};
    border-radius: ${({ theme }) => theme.borderRadius * 2}px;
    background: ${({ theme }) => theme.colors.grayscale.light5};
    transition: border-color ${({ theme }) => theme.transitionTiming}s;
  }

  &.ant-upload-drag:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }

  &.ant-upload-drag-hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    background: ${({ theme }) => theme.colors.primary.light5};
  }

  .ant-upload-drag-container {
    padding: ${({ theme }) => theme.gridUnit * 5}px 0;
  }

  .ant-upload-drag-icon {
    margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;

    .anticon,
    .bi {
      font-size: 48px;
      color: ${({ theme }) => theme.colors.primary.base};
      line-height: 1;
    }
  }

  .ant-upload-text {
    font-size: ${({ theme }) => theme.typography.sizes.l}px;
    font-weight: ${({ theme }) => theme.typography.weights.medium};
    color: ${({ theme }) => theme.colors.grayscale.dark1};
    margin-bottom: ${({ theme }) => theme.gridUnit}px;
  }

  .ant-upload-hint {
    font-size: ${({ theme }) => theme.typography.sizes.s}px;
    color: ${({ theme }) => theme.colors.grayscale.base};
  }
`;

// -- Style: button mode row --

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.gridUnit * 3}px;
  flex-wrap: wrap;
`;

const Hint = styled.span`
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.grayscale.base};
`;

// -- Style: custom file list --

const FileListWrapper = styled.div`
  margin-top: ${({ theme }) => theme.gridUnit * 3}px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.gridUnit * 1.5}px
    ${({ theme }) => theme.gridUnit * 2}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.grayscale.light4};
  margin-bottom: ${({ theme }) => theme.gridUnit * 1.5}px;
`;

const FileName = styled.span`
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.grayscale.dark1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: ${({ theme }) => theme.gridUnit * 2}px;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grayscale.base};
  flex-shrink: 0;
  line-height: 1;
  outline: none;
  box-shadow: none;

  &:hover {
    color: ${({ theme }) => theme.colors.error.base};
    background: ${({ theme }) => theme.colors.grayscale.light3};
  }

  &:active,
  &:focus,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.error.base};
    background: ${({ theme }) => theme.colors.grayscale.light3};
    outline: none;
    box-shadow: none;
    transform: none;
    translate: none;
  }
`;

// -- Props --

export type FileUploadMode = 'dragger' | 'button';

export interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxCount?: number;
  maxSize?: number;
  fileList?: UploadFile[];
  defaultFileList?: UploadFile[];
  onChange?: (info: UploadChangeParam) => void;
  onRemove?: (file: UploadFile) => void | boolean | Promise<void | boolean>;
  customRequest?: (options: any) => void;
  disabled?: boolean;
  mode?: FileUploadMode;
  hint?: string;
  placeholder?: string;
  buttonText?: string;
  icon?: ReactNode;
  showUploadList?: boolean;
  className?: string;
}

// -- Internal --

const ONE_MB = 1024 * 1024;

const toErrorMessage = (fileName: string, maxSize: number): string =>
  `文件 "${fileName}" 超过 ${maxSize}MB 限制`;

const defaultDraggerIcon = <i className="bi bi-cloud-arrow-up" aria-hidden />;

// -- Component --

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  multiple = false,
  maxCount = 1,
  maxSize,
  fileList,
  defaultFileList,
  onChange,
  onRemove,
  disabled = false,
  mode = 'dragger',
  hint,
  placeholder,
  buttonText,
  icon,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const currentList = fileList || defaultFileList || [];

  const handleRemove = useCallback(
    (file: UploadFile) => {
      const newFileList = currentList.filter(f => f.uid !== file.uid);
      onRemove?.(file);
      onChange?.({
        file,
        fileList: newFileList,
      } as UploadChangeParam);
    },
    [currentList, onRemove, onChange],
  );

  const buildChangeParam = useCallback(
    (file: File): UploadChangeParam => {
      const uploadFile: UploadFile = {
        uid: `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: file,
      };
      const newFileList = [...currentList, uploadFile].slice(-maxCount);
      return { file: uploadFile, fileList: newFileList } as UploadChangeParam;
    },
    [currentList, maxCount],
  );

  // 按钮模式：原生 input
  const handleButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleNativeInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (!files || files.length === 0) return;

      Array.from(files).forEach(file => {
        if (maxSize && file.size / ONE_MB > maxSize) {
          // 超过大小限制时通过 error 文件通知父组件
          if (onChange) {
            const errorFile: UploadFile = {
              uid: `error-${Date.now()}`,
              name: file.name,
              size: file.size,
              type: file.type,
              status: 'error',
              response: toErrorMessage(file.name, maxSize),
            };
            onChange({
              file: errorFile,
              fileList: [...currentList, errorFile],
            } as UploadChangeParam);
          }
          return;
        }

        const param = buildChangeParam(file);
        onChange?.(param);
      });

      // 重置 input 值，允许重复选择相同文件
      e.target.value = '';
    },
    [maxSize, onChange, buildChangeParam, currentList],
  );

  // 拖拽模式：Antd Dragger（showUploadList=false, 不自渲染文件列表）
  const handleDraggerChange = useCallback(
    (info: UploadChangeParam) => {
      if (onChange) {
        const latest = info.fileList[info.fileList.length - 1];
        if (!latest) return;

        const file = latest.originFileObj as File;
        if (maxSize && file?.size && file.size / ONE_MB > maxSize) {
          const errorFile: UploadFile = {
            uid: `error-${Date.now()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'error',
            response: toErrorMessage(file.name, maxSize),
          };
          onChange({
            file: errorFile,
            fileList: [...currentList, errorFile],
          } as UploadChangeParam);
          return;
        }

        const param = buildChangeParam(file);
        onChange(param);
      }
    },
    [onChange, buildChangeParam, maxSize, currentList],
  );

  const renderFileList = () => {
    if (currentList.length === 0) return null;
    return (
      <FileListWrapper>
        {currentList.map(file => (
          <FileItem key={file.uid}>
            <FileName title={file.name}>{file.name}</FileName>
            <RemoveBtn
              type="button"
              onClick={() => handleRemove(file)}
              tabIndex={-1}
            >
              <CloseOutlined />
            </RemoveBtn>
          </FileItem>
        ))}
      </FileListWrapper>
    );
  };

  if (mode === 'button') {
    return (
      <div className={className}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleNativeInputChange}
          disabled={disabled}
          style={{ display: 'none' }}
        />
        <ButtonRow>
          <Button
            buttonStyle="secondary"
            onClick={handleButtonClick}
            disabled={disabled}
          >
            <UploadOutlined css={{ marginRight: 0 }} />
            {buttonText || '选择文件'}
          </Button>
          {hint && <Hint>{hint}</Hint>}
        </ButtonRow>
        {renderFileList()}
      </div>
    );
  }

  // dragger mode
  return (
    <div className={className}>
      <StyledDragger
        accept={accept}
        multiple={multiple}
        maxCount={maxCount}
        beforeUpload={(() => undefined) as any}
        onChange={handleDraggerChange}
        customRequest={({ onSuccess }: any) => onSuccess?.('ok')}
        disabled={disabled}
        showUploadList={false}
      >
        <p className="ant-upload-drag-icon">{icon || defaultDraggerIcon}</p>
        <p className="ant-upload-text">
          {placeholder || '点击或拖拽文件到此区域上传'}
        </p>
        {hint && <p className="ant-upload-hint">{hint}</p>}
      </StyledDragger>
      {renderFileList()}
    </div>
  );
};

export default FileUpload;
